import React, {useCallback, useRef, useState} from 'react';
import {Button, Card, Col, Form, Input, Row, Spin, Table, Typography} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import {searchPerson} from '../helpers/account';
import {messageType, showMessages} from '../helpers/message';

import './SearchPerson.less';

// Table columns scaffolding.
const columns = [
    {
        title: <Typography.Text strong>Name</Typography.Text>,
        dataIndex: 'full_name',
        key: 'full_name',
        render: name => {
            return <span className="name-line-item long-text-wrap">{name}</span>
        },
    },
    {
        title: <Typography.Text strong>Email</Typography.Text>,
        dataIndex: 'email',
        key: 'email',
        render: email => {
            return <span className="email-line-item long-text-wrap">{email}</span>
        },
    },
    {
        title: <Typography.Text strong>Status</Typography.Text>,
        dataIndex: 'status',
        key: 'status',
        render: status => {
            return <span className="driver-status-line-item">{status}</span>
        },
    },
];

/**
 * This component is used to search for a person and set states for parent components.
 * It is imported into two places:
 * - AccountPersonAdd which is for an already existent account to add a person.
 * - AccountCreateInformation which is while an account is being created staff can select a preexisting person
 *   or create a new one.
 *
 * personStates is referring to:
 * - AccountPersonAdd using person_id and profile_id. To determine if a profile needs to be created.
 * - AccountCreateInformation using person_id, first and last name, email, and birthdate. To populate the review page.
 *
 * @param personStates
 *
 */
const SearchPerson = ({personStates}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    // Loading and fetch persons data to fill table.
    const [loading, setLoading] = useState(false);
    const [persons, setPersons] = useState([]);
    const [searchName, setSearchName] = useState('');

    // Pagination states.
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(10);

    const [searchForm] = Form.useForm();

    const handleNameChange = e => {
        const {value} = e.target;
        setSearchName(value);
    };

    // Handle submitting the search person single input form.
    const handleSearchFinish = useCallback(async (page, perPage) => {
        setLoading(true);

        try {
            const abortSignal = abortSignalRef.current;
            const persons = await searchPerson(searchName, {page, perPage}, abortSignal);

            if (abortSignal.aborted === false && persons.length !== 0) {
                setPersons(persons.data);
                setPage(persons.meta.current_page);
                setPerPage(persons.meta.per_page);
                setTotal(persons.meta.total);
            }

            setLoading(false);
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
        }
    }, [searchName])

    // On page change, likes to be named onChange.
    const onChange = (page, perPage) => {
        setPage(page);
        setPerPage(perPage);
        handleSearchFinish(page, perPage);
    }

    // Pagination attributes for customization.
    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total,
        onChange,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        onShowSizeChange: onChange,
    };

    return (
        <>
            <Card className="search-form-card">
                <Form
                    form={searchForm}
                    layout="horizontal"
                    colon={false}
                    labelCol={{flex: "60px"}}
                    wrapperCol={{flex: 1}}
                    onFinish={handleSearchFinish}
                >
                    <Row type="flex" justify="space-between">
                        <Col xs={24} sm={24} md={18} lg={18}>
                            <Form.Item className="search-person-form" name="Name" label="Search for an existing person: " rules={[{whitespace: true}]}>
                                <Input className="search-person-form-input-field" placeholder="Please enter name"
                                       onChange={handleNameChange} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Row className="search-person-submit-btn">
                                <Form.Item >
                                    <Button type="primary" htmlType="submit">
                                      Search
                                    </Button>
                                </Form.Item>
                            </Row>
                        </Col>
                    </Row>
                </Form>
            </Card>
            <br/>
            <Spin spinning={loading}>
                <Table
                    rowKey="id"
                    dataSource={persons}
                    columns={columns}
                    rowSelection={{
                        hideSelectAll: true,
                        type: 'radio',
                        selectedRowKeys: [personStates.person_id],
                        onSelect: (person) => {
                            // Better to individually split this up than a convoluted loop where all names need to be identical.
                            if (personStates.set_person_id !== undefined) personStates.set_person_id(person.id);
                            if (personStates.set_first_name !== undefined) personStates.set_first_name(person.first_name);
                            if (personStates.set_last_name !== undefined) personStates.set_last_name(person.last_name);
                            if (personStates.set_birth_date !== undefined) personStates.set_birth_date(dayjs(person.birth_date).tz());
                            if (personStates.set_user_email !== undefined) personStates.set_user_email(person.email);
                            if (personStates.set_profile_id !== undefined) personStates.set_profile_id(person.account_linked_profile_id);
                        }
                    }}
                    bordered={false}
                    title={() => <Typography.Text>Users</Typography.Text>}
                    className="search-person-table responsive-table"
                    pagination={paginationAttr}
                />
            </Spin>
        </>
    )
}

SearchPerson.propTypes = {
    personStates: PropTypes.object.isRequired,
};

export default SearchPerson;
